/*
  Caption tracking on the doors section.

  The caption row rides the pointer up and down its photograph, and fades out
  when the pointer leaves. Horizontal position is fixed; only the vertical
  follows, so the caption and the destination stay on the crop's own margins
  rather than wandering around inside it.

  Why a CSS transition here and a spring in cala-card-cursor.js.

  That script chases a small disc across two axes and stretches it by its own
  velocity, which is a thing a transition genuinely cannot do: it replays one
  curve regardless of how the pointer got there. This is a row of type sliding
  on one axis, where a fixed ease is exactly right and reads as a soft follow.
  A spring here would be forty lines of arithmetic to produce a lag, plus a
  requestAnimationFrame loop running the whole time a pointer rests on a door.

  The script only ever moves the caption. It never reveals it, and it is not
  what makes the copy reachable: CSS shows the row at rest wherever there is no
  hover to give, so a phone, a failed request and a blocked script all land on
  the same readable state. Nothing here is required for the section to work.

  Design notes:
  - Delegated on document, so a merchant adding a fourth door in the editor
    gets it without a rebind.
  - Mouse and a fine pointer only. A touch device has no pointer to follow, and
    pen input would fire this on every tap.
  - Skipped entirely under reduced motion. The row then stays where CSS puts
    it, permanently visible, so the fact it carries is still reachable. That is
    the end state the interaction rules in CLAUDE.md ask for.
  - Seeded on entry with the transition suppressed for a frame, so the row
    appears at the pointer rather than sweeping in from wherever the last
    visitor left it.
*/
(function () {
  var FINE = '(hover: hover) and (pointer: fine)';
  var CALM = '(prefers-reduced-motion: reduce)';

  /* Matches the note's own inline inset, so the row keeps an equal margin at
     the top and bottom of the crop as well as at the sides. */
  var PAD = 14;

  /*
    How close to an edge the pointer gets before the row starts going. The row
    stops dead at its clamp and the remaining travel is spent fading, so it
    leaves rather than sitting pinned against the edge being nudged at.

    Capped as a fraction as well as an absolute, because a fixed 72px zone at
    each end of a 253px crop would put more than half the picture in permanent
    fade. The fraction governs short crops, the pixel value governs tall ones.
  */
  var FADE_MAX = 72;
  var FADE_FRACTION = 0.18;

  function allowed(event) {
    return (
      event.pointerType === 'mouse' &&
      window.matchMedia(FINE).matches &&
      !window.matchMedia(CALM).matches
    );
  }

  /*
    Centre the row on the pointer, then clamp it inside the crop. Without the
    clamp the row hangs off the top and bottom edges when the pointer reaches
    them, and on a short crop the two limits cross, so the lower bound is
    applied second and wins.

    Past the clamp the row cannot follow any further, so it fades instead. The
    fade runs off the pointer's distance to the nearest edge rather than off the
    row's own position, because the row has already stopped by then and would
    have nothing left to measure.
  */
  function place(media, note, clientY) {
    var box = media.getBoundingClientRect();
    var h = note.offsetHeight;
    var y = clientY - box.top - h / 2;
    var max = box.height - h - PAD;

    if (y > max) y = max;
    if (y < PAD) y = PAD;

    media.style.setProperty('--cc-door-y', y + 'px');

    var zone = Math.min(FADE_MAX, box.height * FADE_FRACTION);
    var nearest = Math.min(clientY - box.top, box.bottom - clientY);
    var fade = zone > 0 ? nearest / zone : 1;

    if (fade > 1) fade = 1;
    if (fade < 0) fade = 0;

    media.style.setProperty('--cc-door-fade', fade);
  }

  function noteIn(media) {
    return media.querySelector('.cala-doors__note');
  }

  document.addEventListener('pointerover', function (event) {
    if (!allowed(event)) return;

    var media = event.target.closest('.cala-doors__media');
    if (!media) return;

    var note = noteIn(media);
    if (!note) return;

    place(media, note, event.clientY);

    /* Two frames, not one. A class added and removed inside the same frame is
       never painted, so the suppression would not happen at all and the row
       would slide in from its previous position. */
    media.classList.add('is-tracking', 'is-seeding');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        media.classList.remove('is-seeding');
      });
    });
  });

  document.addEventListener('pointerout', function (event) {
    var media = event.target.closest('.cala-doors__media');
    if (!media) return;

    /* pointerout also fires moving between children of the crop. Only treat it
       as a real exit once the pointer has left the media box. */
    if (event.relatedTarget && media.contains(event.relatedTarget)) return;

    media.classList.remove('is-tracking', 'is-seeding');
  });

  document.addEventListener(
    'pointermove',
    function (event) {
      if (!allowed(event)) return;

      var media = event.target.closest('.cala-doors__media');
      if (!media || !media.classList.contains('is-tracking')) return;

      var note = noteIn(media);
      if (note) place(media, note, event.clientY);
    },
    { passive: true }
  );
})();
